import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Helmet } from "react-helmet";
import { format, subMonths } from "date-fns";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import Button from "../../components/Buttons/Button";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  startGetAdleCardWaitlistSignups,
  startGetAdvertiserBusinessNamesByIDs,
  startGetAgencyNamesByIDs,
} from "../../actions/adleCardWaitlist";
import {
  startGetAdSpendAdvertisersMeta,
  startGetAdSpendAllTime,
  startGetAdSpendMonthly,
} from "../../actions/ownerAdSpend";
import { LaunchError } from "../../utils/errors";
import {
  buildAdleCardWaitlistRows,
  collectAdvertiserIDsFromWaitlistSignups,
  collectAgencyIDsFromWaitlistSignups,
} from "../../utils/adleCardWaitlist";
import { downloadCsvFile, toCsvSafeCell } from "../../utils/csvDownload";
import AdleCardWaitlistTable from "./components/AdleCardWaitlistTable";
import {
  ADLE_CARD_WAITLIST_ADMIN_SPEND_UNAVAILABLE,
  ADLE_CARD_WAITLIST_ADMIN_TOTAL,
  BUTTON_DOWNLOAD,
  ERROR_OCCURRED,
  OWNER_DASHBOARD_NAV_ADLE_CARD_WAITLIST,
} from "../../i18n/keysTranslations";

const CSV_FIELDS = [
  "creationTime",
  "email",
  "country",
  "agencyID",
  "brand",
  "adSpendAllTimeUSD",
  "adSpendLast3MonthsUSD",
];

const formatCsvCreationTime = (creationTime) => {
  const time = Number(creationTime);
  if (!Number.isFinite(time) || time <= 0) return "";
  return format(new Date(time), "yyyy-MM-dd HH:mm");
};

const formatCsvSpend = (value) => (value == null ? "" : value);

const OwnerAdleCardWaitlistBrief = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { theme } = useSelectorApp((state) => state.ui);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [spendUnavailable, setSpendUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const today = new Date();
      const from = format(subMonths(today, 2), "yyyy-MM");
      const to = format(today, "yyyy-MM");

      try {
        const signupsResult = await dispatch(startGetAdleCardWaitlistSignups());

        if (cancelled) return;

        if (!signupsResult?.ok) {
          setError(true);
          setRows([]);
          setSpendUnavailable(false);
          setLoading(false);
          return;
        }

        const agencyIDs = collectAgencyIDsFromWaitlistSignups(
          signupsResult.data
        );
        const advertiserIDs = collectAdvertiserIDsFromWaitlistSignups(
          signupsResult.data
        );

        const [
          allTimeResult,
          monthlyResult,
          metaResult,
          agencyNamesResult,
          advertiserNamesResult,
        ] = await Promise.all([
          dispatch(startGetAdSpendAllTime()),
          dispatch(startGetAdSpendMonthly({ from, to })),
          dispatch(startGetAdSpendAdvertisersMeta()),
          dispatch(startGetAgencyNamesByIDs(agencyIDs)),
          dispatch(startGetAdvertiserBusinessNamesByIDs(advertiserIDs)),
        ]);

        if (cancelled) return;

        const spendFailed =
          allTimeResult?.ok !== true ||
          monthlyResult?.ok !== true ||
          metaResult?.ok !== true;
        const allTime = spendFailed
          ? null
          : allTimeResult.data == null
            ? { byAdvertiser: {} }
            : allTimeResult.data;
        const monthlyByKey = spendFailed ? null : monthlyResult.data || {};
        const advertisersMeta = metaResult?.ok ? metaResult.data || {} : {};
        const agencyNamesById =
          agencyNamesResult?.ok === true ? agencyNamesResult.data || {} : {};
        const advertiserNamesById =
          advertiserNamesResult?.ok === true
            ? advertiserNamesResult.data || {}
            : {};

        setError(false);
        setSpendUnavailable(spendFailed);
        setRows(
          buildAdleCardWaitlistRows({
            signups: signupsResult.data,
            allTime,
            monthlyByKey,
            advertisersMeta,
            agencyNamesById,
            advertiserNamesById,
          })
        );
        setLoading(false);
      } catch (loadError) {
        if (cancelled) return;
        dispatch(
          LaunchError(loadError, false, [], {
            functionName: "OwnerAdleCardWaitlistBrief.load",
            tags: { feature: "adleCardWaitlist" },
            capture: "exception",
          })
        );
        setError(true);
        setRows([]);
        setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const onDownload = () => {
    const fileName = `adle-card-waitlist-${format(new Date(), "yyyy-MM-dd")}.csv`;
    downloadCsvFile({
      fields: CSV_FIELDS,
      fileName,
      data: rows.map((row) => [
        toCsvSafeCell(formatCsvCreationTime(row.creationTime)),
        toCsvSafeCell(row.email),
        toCsvSafeCell(row.country),
        toCsvSafeCell(row.agencyName || row.agencyID),
        toCsvSafeCell(row.brandName),
        formatCsvSpend(row.adSpendAllTimeUSD),
        formatCsvSpend(row.adSpendLast3MonthsUSD),
      ]),
    });
  };

  return (
    <ContainerPage hasMarginBottom={false}>
      <Helmet>
        <title>
          {`${theme?.nameBusiness || ""} - ${t(OWNER_DASHBOARD_NAV_ADLE_CARD_WAITLIST)}`}
        </title>
      </Helmet>
      <Box sx={{ width: "100%", px: 3, mt: 4, flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <TitlePage sx={{ mb: 0.5 }}>
              {t(OWNER_DASHBOARD_NAV_ADLE_CARD_WAITLIST)}
            </TitlePage>
            <Typography variant="body2" color="text.secondary">
              {t(ADLE_CARD_WAITLIST_ADMIN_TOTAL, { count: rows.length })}
            </Typography>
          </Box>
          <Button
            color="secondary"
            onClick={onDownload}
            disabled={loading || error || rows.length === 0}
          >
            {t(BUTTON_DOWNLOAD)}
          </Button>
        </Box>
        {error ? (
          <Typography color="error" sx={{ mb: 2 }}>
            {t(ERROR_OCCURRED)}
          </Typography>
        ) : null}
        {spendUnavailable ? (
          <Typography color="warning.main" sx={{ mb: 2 }}>
            {t(ADLE_CARD_WAITLIST_ADMIN_SPEND_UNAVAILABLE)}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ width: "100%", px: 3, mb: 4, minWidth: 0 }}>
        <AdleCardWaitlistTable rows={rows} loading={loading} />
      </Box>
    </ContainerPage>
  );
};

export default OwnerAdleCardWaitlistBrief;
